import { useState, useEffect, useCallback } from 'react';
import { FiX, FiBook, FiLoader, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { getStrongDefinition } from '../../utils/bible-api';

/**
 * Componente avanzado de diccionario Strong - Panel inferior
 * Con soporte para hebreo, transliteración, múltiples diccionarios, navegación y referencias
 */
export default function StrongDictionaryAdvanced({ strongNumber, onClose, onNavigateToVerse, displayOptions }) {
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Navegación histórica
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  useEffect(() => {
    loadDefinition(strongNumber);
  }, [strongNumber]);

  const loadDefinition = async (number) => {
    try {
      setLoading(true);
      setError(null);

      const response = await getStrongDefinition(number);
      
      const defData = {
        number: response.data.strongNumber,
        hebrewText: response.data.hebrewText,
        transliteration: response.data.transliteration,
        dictionaries: response.data.dictionaries,
        dictionaryName: response.data.dictionaryName
      };
      
      setDefinition(defData);
      
      // Agregar a historial (solo si no estamos navegando hacia atrás)
      if (historyIndex === -1 || historyIndex === history.length - 1) {
        setHistory(prev => [...prev, number]);
        setHistoryIndex(prev => prev + 1);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error al cargar definición:', error);
      setError('No se pudo cargar la definición');
      setLoading(false);
    }
  };

  // Navegar hacia atrás en el historial
  const navigateBack = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      loadDefinition(history[newIndex]);
    }
  }, [historyIndex, history]);

  // Navegar hacia adelante en el historial
  const navigateForward = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      loadDefinition(history[newIndex]);
    }
  }, [historyIndex, history]);

  // Navegar a otro Strong's
  const handleStrongClick = useCallback((strongNum) => {
    loadDefinition(strongNum);
  }, []);

  // Procesar contenido para agregar hipervínculos
  const processContent = (content) => {
    if (!content) return null;

    const parts = [];
    let lastIndex = 0;

    // Primero procesar Strong's numbers
    const strongRegex = /\b([HG]\d{1,5})\b/g;
    let match;
    
    // Crear un array temporal con todos los matches
    const matches = [];
    
    // Buscar Strong's
    while ((match = strongRegex.exec(content)) !== null) {
      matches.push({
        index: match.index,
        length: match[0].length,
        type: 'strong',
        content: match[0],
        number: match[1]
      });
    }
    
    // Buscar referencias bíblicas (Gén_1:1, Éxo_20:12, etc.)
    const bibleRefRegex = /\b([A-ZÁÉÍÓÚÑa-záéíóúñ]{3,})_(\d{1,3}):(\d{1,3}(?:-\d{1,3})?)\b/g;
    while ((match = bibleRefRegex.exec(content)) !== null) {
      matches.push({
        index: match.index,
        length: match[0].length,
        type: 'bibleRef',
        content: match[0],
        book: match[1],
        chapter: match[2],
        verse: match[3]
      });
    }
    
    // Ordenar matches por índice
    matches.sort((a, b) => a.index - b.index);
    
    // Construir parts array
    matches.forEach(m => {
      // Agregar texto antes del match
      if (m.index > lastIndex) {
        parts.push({
          type: 'text',
          content: content.substring(lastIndex, m.index)
        });
      }
      
      parts.push(m);
      lastIndex = m.index + m.length;
    });
    
    // Agregar texto restante
    if (lastIndex < content.length) {
      parts.push({
        type: 'text',
        content: content.substring(lastIndex)
      });
    }

    // Renderizar parts
    return parts.map((part, idx) => {
      if (part.type === 'strong') {
        return (
          <button
            key={idx}
            onClick={() => handleStrongClick(part.number)}
            className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
            title={`Ver definición de ${part.number}`}
          >
            {part.content}
          </button>
        );
      } else if (part.type === 'bibleRef') {
        return (
          <button
            key={idx}
            onClick={() => onNavigateToVerse && onNavigateToVerse(part.book, part.chapter, part.verse)}
            className="text-green-600 hover:text-green-800 hover:underline"
            title={`Ir a ${part.content}`}
          >
            {part.content}
          </button>
        );
      } else {
        return <span key={idx}>{part.content}</span>;
      }
    });
  };

  return (
    <div className="bg-white border-t-4 border-purple-600 shadow-2xl">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-3 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500 rounded-lg">
              <FiBook className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-bold">
                {definition?.dictionaryName || 'Diccionario Strong'}
              </h3>
              <p className="text-xs text-purple-100">
                {strongNumber}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Navegación histórica */}
            <button
              onClick={navigateBack}
              disabled={historyIndex <= 0}
              className={`p-2 rounded-lg transition-colors ${
                historyIndex <= 0
                  ? 'opacity-30 cursor-not-allowed'
                  : 'hover:bg-purple-500'
              }`}
              title="Atrás"
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={navigateForward}
              disabled={historyIndex >= history.length - 1}
              className={`p-2 rounded-lg transition-colors ${
                historyIndex >= history.length - 1
                  ? 'opacity-30 cursor-not-allowed'
                  : 'hover:bg-purple-500'
              }`}
              title="Adelante"
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-purple-500 rounded-lg transition-colors"
              title="Cerrar diccionario"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 overflow-y-auto h-80">
        {loading && (
          <div className="text-center py-8">
            <FiLoader className="w-8 h-8 text-purple-600 mx-auto mb-3 animate-spin" />
            <p className="text-gray-600 text-sm">Cargando definición...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && definition && (
          <div className="space-y-4">
            {/* Número Strong */}
            <div className="flex items-center justify-between pb-3 border-b">
              <span className="text-2xl font-bold text-purple-600">
                {definition.number}
              </span>
            </div>

            {/* Texto Hebreo */}
            {displayOptions?.showHebrewText && definition.hebrewText && (
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                <p className="text-xs font-medium text-blue-900 mb-1">
                  Texto Hebreo:
                </p>
                <p 
                  className="text-3xl text-blue-800 font-hebrew text-right"
                  dir="rtl"
                  style={{ fontFamily: 'SBL Hebrew, David, Narkisim, Times New Roman' }}
                >
                  {definition.hebrewText}
                </p>
              </div>
            )}

            {/* Transliteración */}
            {displayOptions?.showTransliteration && definition.transliteration && (
              <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                <p className="text-xs font-medium text-green-900 mb-1">
                  Transliteración:
                </p>
                <p className="text-xl text-green-800 italic font-semibold">
                  {definition.transliteration}
                </p>
              </div>
            )}

            {/* Diccionarios */}
            {definition.dictionaries && definition.dictionaries.length > 0 && (
              <div className="space-y-4">
                {definition.dictionaries.map((dict, idx) => (
                  <div key={idx} className="border-t pt-4 first:border-t-0 first:pt-0">
                    <h4 className="text-sm font-bold text-purple-900 mb-2 uppercase tracking-wide">
                      {dict.name}
                    </h4>
                    <div className="text-gray-800 text-sm leading-relaxed">
                      {processContent(dict.content)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t px-4 py-2 bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span>Navegación: {history.length} consulta(s)</span>
          {history.length > 1 && (
            <span>• Posición: {historyIndex + 1}/{history.length}</span>
          )}
        </div>
        <button
          onClick={onClose}
          className="px-3 py-1 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}

